import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { notification } from "antd";
import {
  IUpsertConsentResponse,
  IUpsertConsentVariables,
} from "../../../models/Interface/User.interface";
import axiosInstance, { CustomAxiosError } from "../../../utils/axios";
import { IErrorResponse, QueryKeys } from "../../querys/querys.enum";
import { MutationKeys } from "../../mutation/mutation.enum";

const useUpsertConsent = (
  options?: Partial<
    UseMutationOptions<
      IUpsertConsentResponse,
      AxiosError<IErrorResponse>,
      IUpsertConsentVariables
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    IUpsertConsentResponse,
    AxiosError<IErrorResponse>,
    IUpsertConsentVariables
  >({
    mutationKey: [MutationKeys.UPSERT_CONSENT],
    mutationFn: async ({ userId, data }) => {
      try {
        const response = await axiosInstance.post<IUpsertConsentResponse>(
          `/users-consents/${userId}`,
          data
        );
        return response.data;
      } catch (error) {
        console.error("Error in useUpsertConsent mutationFn:", error);
        throw error;
      }
    },
    retry: 0,
    ...options,
    onSuccess: (data, variables, cx) => {
      try {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.USER_CONSENTS, { userId: variables.userId }],
        });
        notification.success({
          message: "Consent saved",
          description: data.message || "Consent updated successfully",
          placement: "top",
        });
        if (options?.onSuccess) {
          options.onSuccess(data, variables, cx);
        }
      } catch (successError) {
        console.error("Error in onSuccess handler:", successError);
      }
    },
    onError: (error: CustomAxiosError, variables, context) => {
      try {
        console.error("Upsert consent error:", error?.response?.data);
        notification.error({
          message: "Failed to save consent",
          description: error?.response?.data?.message || "Something went wrong",
          placement: "top",
        });
        if (options?.onError) {
          options.onError(
            error as AxiosError<IErrorResponse>,
            variables,
            context
          );
        }
      } catch (errorHandlingError) {
        console.error("Error in onError handler:", errorHandlingError);
      }
    },
  });
};

export default useUpsertConsent;
